import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Tag, Tooltip } from 'antd';
import type { ColumnType, FilterValue } from 'antd/es/table/interface';

import type { ITest, ITestingParticipantsStats } from '@/api/endpoints/schools/schoolSelectionPanel';
import { ETestStatus } from '@/shared/types';

import { OptionsCell } from '../_components/OptionsCell/OptionsCell';

import { TEST_STATUS_TO_TAG_SETTINGS } from './consts';
import styles from './TestingParticipantsPage.module.scss';

export const getColumnsData = ({
  filters,
  openModerateModalCb,
  setItemToOperate,
}: {
  filters: Record<string, FilterValue | null>;
  openModerateModalCb: VoidFunction;
  setItemToOperate: (data: ITest | null) => void;
}) =>
  [
    {
      title: 'ФИО',
      dataIndex: ['user', 'full_name'],
      key: 'name',
      ellipsis: true,
      render: (_, { user: { full_name: fullName }, block_user: isUserBlocked }) => (
        <div className={styles['name-wrapper']}>
          <p>{fullName}</p>
          {isUserBlocked ? (
            <Tooltip title="Отклонён СБ">
              <ExclamationCircleOutlined className={styles['user-block-icon']} />
            </Tooltip>
          ) : null}
        </div>
      ),
    },
    {
      title: 'Email',
      dataIndex: ['user', 'email'],
      key: 'email',
      ellipsis: true,
    },
    {
      title: 'Телефон',
      dataIndex: ['user', 'phone'],
      key: 'phone',
      ellipsis: true,
      render: (_, { user }) => user.phone || '-',
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      ellipsis: true,
      filteredValue: filters.status || null,
      filters: [
        {
          text: TEST_STATUS_TO_TAG_SETTINGS[ETestStatus.NotStarted].text,
          value: ETestStatus.NotStarted,
        },
        {
          text: TEST_STATUS_TO_TAG_SETTINGS[ETestStatus.InProgress].text,
          value: ETestStatus.InProgress,
        },
        {
          text: TEST_STATUS_TO_TAG_SETTINGS[ETestStatus.OnReview].text,
          value: ETestStatus.OnReview,
        },
        {
          text: TEST_STATUS_TO_TAG_SETTINGS[ETestStatus.TestPassed].text,
          value: ETestStatus.TestPassed,
        },
        {
          text: TEST_STATUS_TO_TAG_SETTINGS[ETestStatus.TestFailed].text,
          value: ETestStatus.TestFailed,
        },
        {
          text: TEST_STATUS_TO_TAG_SETTINGS[ETestStatus.Approved].text,
          value: ETestStatus.Approved,
        },
        {
          text: TEST_STATUS_TO_TAG_SETTINGS[ETestStatus.Rejected].text,
          value: ETestStatus.Rejected,
        },
      ],
      render: (_, { status }) => (
        <Tag
          className={styles.tag}
          color={TEST_STATUS_TO_TAG_SETTINGS[status].color}
          icon={TEST_STATUS_TO_TAG_SETTINGS[status].icon}
        >
          {TEST_STATUS_TO_TAG_SETTINGS[status].text}
        </Tag>
      ),
    },
    {
      title: '',
      dataIndex: 'options',
      key: 'options',
      width: 64,
      render: (_, participantData) => (
        <OptionsCell
          openModerateModalCb={() => {
            setItemToOperate(participantData);
            openModerateModalCb();
          }}
          participantStatus={participantData.status}
          resultNotifiedAt={participantData.result_notified_at}
          decisionNotifiedAt={participantData.decision_notified_at}
        />
      ),
      fixed: 'right',
    },
  ] as ColumnType<ITest>[];

export const formatTestingStats = (testingStats: ITestingParticipantsStats | undefined) => {
  if (!testingStats) {
    return;
  }

  const {
    total,
    not_started: notStarted,
    in_progress: inProgress,
    on_review: onReview,
    test_passed: testPassed,
    test_failed: testFailed,
    in_reserve: inReserve,
    approved,
    rejected,
  } = testingStats;

  return [
    {
      id: 1,
      title: 'Всего',
      value: total,
    },
    {
      id: 2,
      title: 'Не приступил',
      value: notStarted,
    },
    {
      id: 3,
      title: 'В процессе',
      value: inProgress,
    },
    {
      id: 4,
      title: 'На проверке',
      value: onReview,
    },
    {
      id: 5,
      title: 'Тест сдан',
      value: testPassed,
    },
    {
      id: 6,
      title: 'Тест не сдан',
      value: testFailed,
    },
    {
      id: 7,
      title: 'Резерв',
      value: inReserve,
    },
    {
      id: 8,
      title: 'Одобрено',
      value: approved,
    },
    {
      id: 9,
      title: 'Отклонено',
      value: rejected,
    },
  ];
};
